import { defineComponent as _defineComponent } from 'vue'
import "@/scss/libraries/_fa-font-face.scss";
import "@/scss/_color-overrides.scss";
import { useFormio } from "@/settings/formioInstanceSettings";
import {
  addCssFileToDom,
  addFormMessage,
  applyButtonsStyles,
  applyButtonsText,
  applyContainerStyles,
  applyHeadlineStyles,
  getFormJsonUrl,
  getFormSubmitUrl,
  getMigratedSchema,
  getPrefilledSchemaBasedOnResponseHeaders,
  getSchemaByPrefill,
} from "@/features/utils";
import { componentsAttachList } from "@/settings/componentsAttachMethods";
import { formSettings } from "@/settings/formSettings";
import { Utils } from "formiojs";
import { onMounted, Ref, ref } from "vue";
import { FormResponse } from "@/types/FormResponse";
import {
  getLangCode,
  setFormioI18nSetting,
} from "@/features/formioTranslation";
import { FontItem } from "@/types/FontItem";
import { externalFontLink } from "@/constants/general";


export default /*#__PURE__*/_defineComponent({
  __name: 'CreateUserForm.ce',
  props: {
  dataSource: {
    type: String,
  },
  dataFormId: {
    type: String,
  },
},
  setup(__props, { expose }) {
  expose();

const props = __props;


const formContainer = ref("formContainer") as Ref<HTMLElement>;
const error = ref("");
const loading = ref(true);

onMounted(async () => {
  if (!props.dataSource || !props.dataFormId) {
    error.value = "Missing required form initialization parameters";
    loading.value = false;

    return;
  }

  const jsonUrl = getFormJsonUrl({ dataSourceHost: props.dataSource, formId: props.dataFormId });
  const submitUrl = getFormSubmitUrl({ dataSourceHost: props.dataSource, formId: props.dataFormId });
  // NOTE: This values can be not callable, so we need to additionally check if country is supported in phone component
  let formJSONResponseHeaders: Headers | null = null;

  let json;

  try {
    const response = await fetch(jsonUrl);

    if (response.ok) {
      json = await response.json();
      formJSONResponseHeaders = response.headers;
    } else {
      error.value = "Form Not Found";
      loading.value = false;
    }
  } catch (e) {
    error.value = "Form Not Found";
    loading.value = false;
  }

  if (error.value) {
    return;
  }

  let fontList;
  try {
    fontList = await (await fetch(externalFontLink)).json();
  } catch (e) {
    console.warn("Fonts are not loaded");
  }

  if (fontList) {
    (fontList as Array<FontItem>).forEach((item) => {
      addCssFileToDom(item.url);
    });
  }

  const lang = getLangCode(navigator.language);
  await setFormioI18nSetting(lang, [formSettings]);
  const schema = getPrefilledSchemaBasedOnResponseHeaders(
    getMigratedSchema(getSchemaByPrefill(json)),
    formJSONResponseHeaders,
  );
  const formioInstance = useFormio();

  // assign attach methods
  for (const componentsAttachListKey in componentsAttachList) {
    const originAttach = formioInstance.Components.components[componentsAttachListKey].prototype.attach;
    
    formioInstance.Components.components[componentsAttachListKey].prototype.attach = function (element: HTMLElement) {
      componentsAttachList[componentsAttachListKey].forEach(
        (fn) => fn(this, element, schema, props.dataFormId)
      );

      return originAttach.call(this, element);
    };
  }

  formioInstance
    .createForm(
      formContainer.value,
      schema,
      JSON.parse(JSON.stringify(formSettings)),
    )
    .then((formInstance) => {
      applyContainerStyles(formInstance.schema, formContainer.value, "element");
      applyHeadlineStyles(
        formInstance.schema,
        formInstance.i18next.t("Headline"),
        formContainer.value,
        "element"
      );
      applyButtonsStyles(
        formInstance.schema,
        formContainer.value,
        "element",
        props.dataFormId
      );
      applyButtonsText(formInstance.schema, formContainer.value, "element");

      // add manual dropdown click listeners (not work in shadow DOM)
      const handleDropdown = (e) => {
        e.stopPropagation();

        const dropdownList = e.currentTarget.nextElementSibling;

        dropdownList.classList.toggle("is-active");

        const searchInput = dropdownList.querySelector(".choices__input.choices__input--cloned");

        searchInput.focus();
        searchInput.click();
      };
      const addDropdownListeners = () => {
        const dropdownContainers = formContainer.value.querySelectorAll(
          ".form-control.ui.fluid.selection.dropdown"
        );

        dropdownContainers.forEach((dropdownContainer) => {
          dropdownContainer.addEventListener("click", handleDropdown);
        });

        const searchInputs = formContainer.value.querySelectorAll(
          ".choices__input.choices__input--cloned"
        );

        searchInputs.forEach((searchInput) => {
          searchInput.addEventListener("click", (e) => {
            e.stopPropagation();
          });
        });
      };

      addDropdownListeners();

      formInstance.on("render", function () {
        applyContainerStyles(
          formInstance.schema,
          formContainer.value,
          "element"
        );
        applyHeadlineStyles(
          formInstance.schema,
          formInstance.i18next.t("Headline"),
          formContainer.value,
          "element"
        );
        applyButtonsStyles(formInstance.schema, formContainer.value, "element");
        applyButtonsText(formInstance.schema, formContainer.value, "element");
      });
      formInstance.on("submitError", (errors: unknown) => {
        console.error("Form submit errors", errors);
      });
      formInstance.on("nextPage", () => {
        addDropdownListeners();
      });
      formInstance.on("prevPage", () => {
        addDropdownListeners();
      });
      formInstance.nosubmit = true;
      formInstance.on("submit", (submission: any) => {
        // clear prev alert messages
        const messagesElements = formContainer.value.querySelectorAll(".formio-component.formio-component-label-hidden .alert");
        messagesElements.forEach((messagesElement) => messagesElement.remove());
        // set right data names
        const data: any = {};

        for (const dataKey in submission.data) {
          if (dataKey === "submit") {
            break;
          }

          const component = Utils.searchComponents(
            formInstance.schema.components,
            {
              key: dataKey,
            }
          );
          const componentSchema = component.length ? component[0] : null;
          data[componentSchema.originalKey] = submission.data[dataKey];
        }

        const requestData: Partial<Record<string, any>> = {
          body: JSON.stringify(data),
          headers: {
            "content-type": "application/json",
          },
          method: "POST",
          mode: "cors",
        };

        return formioInstance
          .fetch(submitUrl, requestData)
          .then(async (response: any) => {
            const res: FormResponse = await response.json();
            if (res.redirection_url) {
              window.location.href = res.redirection_url;
            }
            formInstance.setPage(0);
            if (res.message) {
              const messageParentElement =
                formContainer.value.querySelector(".formio-component");
              addFormMessage(
                messageParentElement as Element,
                res.message,
                res.error ? "alert-danger" : "alert-success"
              );
            }
            if (res.success) {
              formInstance.emit("submitDone", submission);
              formInstance.resetValue();
            }
          })
          .catch(() => {
            formInstance.setPage(0);
            const messageParentElement =
              formContainer.value.querySelector(".formio-component");
            addFormMessage(
              messageParentElement as Element,
              "Something went wrong",
              "alert-danger"
            );
          });
      });
      loading.value = false;
    });
});

const __returned__ = { props, formContainer, error, loading, get useFormio() { return useFormio }, get addCssFileToDom() { return addCssFileToDom }, get addFormMessage() { return addFormMessage }, get applyButtonsStyles() { return applyButtonsStyles }, get applyButtonsText() { return applyButtonsText }, get applyContainerStyles() { return applyContainerStyles }, get applyHeadlineStyles() { return applyHeadlineStyles }, get getFormJsonUrl() { return getFormJsonUrl }, get getFormSubmitUrl() { return getFormSubmitUrl }, get getMigratedSchema() { return getMigratedSchema }, get getPrefilledSchemaBasedOnResponseHeaders() { return getPrefilledSchemaBasedOnResponseHeaders }, get getSchemaByPrefill() { return getSchemaByPrefill }, get componentsAttachList() { return componentsAttachList }, get formSettings() { return formSettings }, get Utils() { return Utils }, onMounted, Ref, ref, get FormResponse() { return FormResponse }, get getLangCode() { return getLangCode }, get setFormioI18nSetting() { return setFormioI18nSetting }, get FontItem() { return FontItem }, get externalFontLink() { return externalFontLink } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})